import { forwardRef } from 'react';

import styled from '@emotion/styled';
import { format } from 'date-fns';

import { helper } from '@myrealtrip/frontend-weblog';
import { media } from '@myrealtrip/modash';
import { IcoReset } from '@myrealtrip/web-icons';
import {
  ButtonGeneral,
  ButtonText,
  PriceSingleDatePicker,
  PriceRangeDatePicker,
  FormattedValue,
  Text,
  RangeDate,
} from '@myrealtrip/web-ui';

import Show from '~components/Show';
import { WeblogEventName, WeblogPageCategory, WeblogScreenName } from '~constants/weblog';
import { useDetermineMobile } from '@myrealtrip/device-detect';

import type { RoundTripJourneyType } from '~models/Transportation/PointToPointTicket';

import DepartureTimeOption from './DepartureTimeOption';

const { sendClickLog } = helper;

interface Props {
  isSingleTrip: boolean;
  startDate: Date | undefined;
  endDate?: Date;
  prices?: Record<string, string>;
  specialPriceDates?: Date[];
  showTimeOption?: boolean;
  outboundStartTime?: string | null;
  inboundStartTime?: string | null;
  onApply: () => void;
  onChangeDate: (date: Date, formattedValue: FormattedValue) => void;
  onRangeChangeDate: (rangeDate: RangeDate) => void;
  onReset: () => void;
  onChangeTime?: (type: RoundTripJourneyType, time: string) => void;
}

const MAX_MONTHS = 13;

const CalendarBody = forwardRef<HTMLUListElement, Props>(
  (
    {
      isSingleTrip,
      startDate,
      endDate,
      prices = {},
      specialPriceDates,
      showTimeOption = false,
      outboundStartTime,
      inboundStartTime,
      onApply,
      onChangeDate,
      onRangeChangeDate,
      onReset,
      onChangeTime,
    }: Props,
    timeOptionRef,
  ) => {
    const isMobile = useDetermineMobile();

    const today = new Date();
    const maxDate = new Date(today);
    maxDate.setMonth(today.getMonth() + MAX_MONTHS);

    const handleApply = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      sendClickLog({
        pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
        screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
        eventName: WeblogEventName.CALENDAR_APPLY,
        itemKind: 'button',
        itemName: '일정 적용',
        departureDate: startDate ? format(startDate, 'yyyy-MM-dd') : '',
        ...(!isSingleTrip && { arrivalDate: endDate ? format(endDate, 'yyyy-MM-dd') : '' }),
      });

      onApply();
    };

    const handleReset = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      onReset();
    };

    const customHeader = ({ monthDate }: { monthDate: Date }) => (
      <CustomHeader>
        <CurrentMonth>{format(monthDate, 'yyyy년 MM월')}</CurrentMonth>
      </CustomHeader>
    );

    return (
      <>
        {isSingleTrip ? (
          <PriceSingleDatePicker
            outline={false}
            isOpen
            scrollable
            width="100%"
            height={isMobile ? '100%' : 440}
            selected={startDate}
            minDate={today}
            maxDate={maxDate}
            monthsShown={MAX_MONTHS}
            prices={prices}
            specialPriceDates={specialPriceDates}
            showPreviousMonths={false}
            onChange={onChangeDate}
            renderCustomHeader={customHeader}
            variant="secondary"
          />
        ) : (
          <PriceRangeDatePicker
            outline={false}
            isOpen
            scrollable
            width="100%"
            height={isMobile ? '100%' : 440}
            rangeDate={[startDate, endDate]}
            minDate={today}
            maxDate={maxDate}
            monthsShown={MAX_MONTHS}
            prices={prices}
            specialPriceDates={specialPriceDates}
            startDateLabel={prices ? '' : '가는날'}
            endDateLabel={prices ? '' : '오는날'}
            showPreviousMonths={false}
            onChangeRangeDate={onRangeChangeDate}
            renderCustomHeader={customHeader}
            variant="secondary"
          />
        )}
        <CTAContainer>
          <Show when={showTimeOption}>
            <>
              <DepartureTimeOption
                isSingleTrip={isSingleTrip}
                outboundStartTime={outboundStartTime}
                inboundStartTime={inboundStartTime}
                onChangeTime={onChangeTime || (() => {})}
                ref={timeOptionRef}
              />
              <Divider />
            </>
          </Show>
          <ButtonsContainer>
            <ResetButtonText leftIcon={<IcoReset />} onClick={handleReset}>
              초기화
            </ResetButtonText>
            <ButtonGeneral
              size="medium"
              shape="rectangle"
              disabled={isSingleTrip ? !startDate : !startDate || !endDate}
              onClick={handleApply}
            >
              <Text typography="headlineNormal15">일정 적용</Text>
            </ButtonGeneral>
          </ButtonsContainer>
        </CTAContainer>
      </>
    );
  },
);

CalendarBody.displayName = 'CalendarBody';

const CustomHeader = styled.div`
  padding: 20px 24px 4px;
`;

const CurrentMonth = styled.strong`
  ${({ theme }) => theme.typography.headlineNormal16};
  line-height: normal;
`;

const CTAContainer = styled.footer`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  display: flex;
  z-index: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme: { palettes } }) => palettes.white};
  padding: 0 0 constant(safe-area-inset-bottom);
  padding: 0 0 env(safe-area-inset-bottom);
  box-shadow: ${({ theme: { shadows } }) => shadows.E100V};

  ${media.minMobile} {
    position: sticky;
    height: auto;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme: { colors } }) => colors.gray.s80};
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
`;

const ResetButtonText = styled(ButtonText)`
  color: ${({ theme: { palettes } }) => palettes.gray[1000]};
  ${({ theme: { typography } }) => typography.paragraphBold14};
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
`;

export default CalendarBody;
